import { useNavigate, useParams } from 'react-router-dom';

import { Box, Typography } from '@mui/material';

import { PlainMessage } from '@bufbuild/protobuf';
import {
  ChapterCard,
  ChipBadge,
  DashboardGrid,
  IStyles,
  SubjectCard,
  convertBytesToHumanReadable,
  deleteDownloadedChapter,
  deserify,
  findDownloadedSubjectByParams,
  getMediaBasePath,
  getSizeOfNode,
  pxToRem,
  pxTovW,
} from '@geneo2-web/shared-ui';
import { DownloadedSubject } from '@protos/content_management/content.db_pb';
import { useEffect, useState } from 'react';
import { useGlobalContext } from '../../../../app/Context/GlobalContextProvider';
import { DeleteButton } from '../../../../components/DeleteButton';
import { useAppSelector } from '../../../../reduxStore/reduxHooks';
import {
  HOME,
  OFFLINE_LEARN_TOPIC_SELECTION,
} from '../../../../routeHandling/RoutesNomenclature';
import { getSubjectsMap } from '../../../../utils/icons';
import { interactionEvent } from '../../../Auth/Login/login_logout.events';
import { useDownloadContext } from '../../../../app/Context/DownloadContextProviderV2';

const styles: IStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100vw',
    boxSizing: 'border-box',
    padding: { md: `${pxTovW(50)} ${pxTovW(240)}` },
    gap: { xs: pxToRem(0), md: pxTovW(40) },
  },
  subjectBox: {
    width: '100%',
    display: 'flex',
    gap: { xs: pxToRem(15), md: 0 },
    boxSizing: 'border-box',
    flexDirection: { xs: 'column', md: 'row' },
    justifyContent: 'space-between',
    padding: { xs: pxToRem(20), md: 0 },
  },
  onGoing: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(15), md: pxTovW(10) },
    mt: { md: pxTovW(15) },
  },
  allChaptersContainer: {
    width: '100%',
    boxSizing: 'border-box',

    '& .grid': {
      padding: { xs: pxToRem(12), md: pxTovW(12) },
    },
    '& .grid > div': {
      borderBottom: '1px solid #B8B8B8',
    },
    '& .grid:not(:nth-of-type(3n))': {
      borderRight: { xs: '1px solid none', md: '1px solid #B8B8B8' },
      borderImage:
        'linear-gradient(to bottom, white 10%,#B8B8B8 10% 90%,white 90% 100%) 1',
    },
  },

  lowerBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(15), md: pxTovW(25) },
  },

  textWithBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(10) },
    padding: { xs: pxToRem(20), md: 0 },
  },
};

export default function LearnDashboardOffline() {
  const navigate = useNavigate();
  const { subject_id } = useParams();
  const subjectId = Number(subject_id);
  const { userInfo } = deserify(useAppSelector((state) => state.auth));
  const [downloadedSubject, setDownloadedSubject] = useState<
    PlainMessage<DownloadedSubject>
  >(new DownloadedSubject());
  const learnSubjects = userInfo?.learnSubjects || [];
  const subMap = getSubjectsMap(learnSubjects);
  const selectedSubject = subMap[subjectId] || {};

  const chapters = downloadedSubject.chapters || {};
  // Filter out chapters with lesson plans of current teacher and section
  const chaptersArray =
    Object.values(chapters)
      .sort((a, b) => a.order - b.order)
      .filter((chap) => {
        const topics = chap.downloadedTopics || {};
        let hasDownloadedLps = false;
        Object.values(topics)
          .sort((a, b) => a.order - b.order)
          .filter((top) => {
            const lPs = top.downloadedLessonPlans || {};
            const lPsArray = Object.values(lPs);
            if (lPsArray.length) {
              hasDownloadedLps = true;
            }
          });
        return hasDownloadedLps;
      }) || [];

  const { setSelectedFunction } = useGlobalContext();
  const { isOffline } = useDownloadContext();

  const backButtonClick = () => {
    navigate(HOME);
  };

  useEffect(() => {
    updateDownloadedSubjectData();
    setSelectedFunction(() => backButtonClick);
    return () => {
      setSelectedFunction(null);
    };
  }, []);

  const updateDownloadedSubjectData = async () => {
    const indexDbData = await findDownloadedSubjectByParams({
      subjectId: subjectId,
    });
    if (indexDbData.length > 0) {
      const data = indexDbData[0];
      setDownloadedSubject(data);
    }
  };

  return (
    <Box sx={styles.root}>
      {/* <ConfigSelector /> */}
      {isNaN(subjectId) ? null : (
        <>
          <Box sx={styles.subjectBox}>
            <SubjectCard
              image={getMediaBasePath(
                downloadedSubject.bookImageUrl || selectedSubject.iconUrl,
                'processedMediaBucket'
              )}
              title={downloadedSubject.name || selectedSubject.subjectName}
              subTitle={`Class ${
                userInfo?.classSectionDetails?.className || ''
              } ${userInfo?.classSectionDetails?.sectionName || ''}`}
            />
          </Box>

          <Box sx={styles.lowerBox}>
            <Box sx={styles.textWithBadge}>
              <Typography variant="h2">Chapters</Typography>
              <Typography variant="h4">
                <ChipBadge
                  label={chaptersArray.length}
                  color="primary"
                  size="small"
                />
              </Typography>
            </Box>

            <Box
              sx={{
                marginBottom: { xs: pxToRem(20), md: 0 },
              }}
            >
              <DashboardGrid
                items={chaptersArray.map((chapter, index) => {
                  return (
                    <ChapterCard
                      withArrow
                      variant="normal"
                      mainHeading={`${chapter.order}. ${chapter.chapterTitle}`}
                      offineSubText={convertBytesToHumanReadable(
                        getSizeOfNode(downloadedSubject, chapter.chapterId)
                      )}
                      rootStyle={{ width: '100%' }}
                      cardClickHandler={async () => {
                        navigate(
                          `${OFFLINE_LEARN_TOPIC_SELECTION}/${subjectId}/${chapter.chapterId}`
                        );
                        await interactionEvent({
                          url: 'Student_Learn_dashboard',
                          context: 'All_Chapters',
                          name: 'CHAPTER_OPEN',
                          isOffline: isOffline,
                        });
                      }}
                      image={getMediaBasePath(
                        chapter.posterImagesUrl,
                        'processedMediaBucket'
                      )}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: { xs: '50%', md: 0 },
                          right: { xs: pxToRem(12), md: 0 },
                          transform: { xs: 'translateY(-50%)', md: 'initial' },
                        }}
                      >
                        <DeleteButton
                          title={`${chapter.order}. ${chapter.chapterTitle}`}
                          onClick={async () => {
                            if (isNaN(subjectId) || !chapter.chapterId) {
                              return;
                            }
                            await deleteDownloadedChapter({
                              subjectId,
                              chapterId: chapter.chapterId,
                              userId: userInfo?.studentProfileId.toString(),
                            });
                            await updateDownloadedSubjectData();
                            await interactionEvent({
                              url: 'Student_learn_dashboard',
                              context: 'chapters',
                              name: 'DELETE',
                              isOffline: isOffline,
                            });
                          }}
                        />
                      </Box>
                    </ChapterCard>
                  );
                })}
                WebNoOfCols={3}
                mobileNoOfCols={1}
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

const newItems = [];
